import UnwrittenEncounterReview from "@/ai-coding-outputs/admin-coding/UnwrittenEncounterReview";
import HumanCodingReview from "@/ai-coding-outputs/human-coding/HumanCodingReview";
import { AuthProvider } from "@/auth/AuthProvider";
import ClinicalCodeTransformationsPage from "@/clinical-codes-transformations/ClinicalCodeTransformations";
import { ROUTES as CLINICAL_CODES_TRANSFORMATIONS_ROUTES } from "@/clinical-codes-transformations/navigation";
import ClinicalCoding from "@/clinical-codes/ClinicalCoding";
import { ROUTES as CLINICAL_CODES_ROUTES } from "@/clinical-codes/navigation";
import { Toaster } from "@/components/ui/sonner";
import { AppLayout } from "@/layout/AppLayout";
import Index from "@/pages/Index";
import NoRoleAssigned from "@/pages/NoRoleAssigned";
import NotFound from "@/pages/NotFound";
import CustomerManagementPage from "@/pages/admin/CustomerManagementPage";
import Datasets from "@/pages/admin/Datasets";
import DemoManagement from "@/pages/admin/DemoManagement";
import EncounterMrnLookup from "@/pages/admin/EncounterMrnLookup";
import Fields from "@/pages/admin/Fields";
import Settings from "@/pages/admin/Settings";
import TemplateEditor from "@/pages/admin/TemplateEditor";
import Templates from "@/pages/admin/Templates";
import CliAuthorize from "@/pages/CliAuthorize";
import Auth from "@/pages/auth/Auth";
import UserSettings from "@/user/Settings";
import { ROUTES as USER_ROUTES } from "@/user/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, BrowserRouter as Router, Routes } from "react-router";
import CodingDashboard from "./admin-coding-dashboard/CodingDashboard";
import { GapAnalysisSection } from "./admin-coding-dashboard/GapAnalysisSection";
import { ROUTES as ADMIN_CODING_ROUTES } from "./admin-coding-dashboard/navigation";
import HumanReviewWorklistNext from "./ai-coding-outputs/human-coding/HumanReviewWorklistNext";
import { ROUTES as AI_CODING_OUTPUTS_ROUTES } from "./ai-coding-outputs/navigation";
import AdminGuard from "./auth/AdminGuard";
import ClinicalCoderAdminGuard from "./auth/ClinicalCoderAdminGuard";
import ClinicalCoderGuard from "./auth/ClinicalCoderGuard";
import TestDataGuard from "./auth/TestDataGuard";
import { CLINICAL_CODE_RULES_ROUTES, ClinicalCodeRulesPage } from "./clinical-code-rules";
import Demos from "./pages/admin/Demos";
import EditDemoScenario from "./pages/admin/EditDemoScenario";
import FieldHelpTexts from "./pages/admin/FieldHelpTexts";
import FieldRules from "./pages/admin/FieldRules";
import RawFilesViewerPage from "./raw-files-viewer/RawFilesViewerPage";
import RawFilesViewerEncounterPage from "./raw-files-viewer/RawFilesViewerEncounterPage";
import { ROUTES as RAW_FILES_VIEWER_ROUTES } from "./raw-files-viewer/navigation";
import RelevanceReviewQueuePage from "./relevance-review/RelevanceReviewQueuePage";
import { FormReadCorrectionsPage } from "./form-read-corrections/FormReadCorrectionsPage";
import { FormReadCorrectionsEncounterPage } from "./form-read-corrections/FormReadCorrectionsEncounterPage";
import { ROUTES as FORM_READ_CORRECTIONS_ROUTES } from "./form-read-corrections/navigation";
import { ROUTES as RELEVANCE_REVIEW_ROUTES } from "./relevance-review/navigation";
import RunDetails from "./test-data-runs/RunDetailsPage";
import TestRuns from "./test-data-runs/TestRunsPage";
import PatientEncounterDetails from "./test-data/PatientEncounterDetails";
import TestData from "./test-data/TestDataPage";
import { ROUTES as TEST_DATA_ROUTES } from "./test-data/navigation";
import WriteBackManagementPage from "./write-back-management/WriteBackManagementPage";
import { ROUTES as WRITE_BACK_MANAGEMENT_ROUTES } from "./write-back-management/navigation";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Routes>
            {/* Standalone routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/no-role" element={<NoRoleAssigned />} />
            <Route path="/cli/authorize" element={<CliAuthorize />} />
            <Route path="/demos" element={<Demos />} />
            {/* Demo routes */}
            {/* Admin and demo routes with shared layout */}
            <Route path="" element={<AppLayout />}>
              <Route path="user">
                <Route path={`${USER_ROUTES.settings.slug}`} element={<UserSettings />} />
              </Route>

              <Route path="admin" element={<AdminGuard />}>
                <Route path="demo">
                  <Route path="settings" element={<Settings />} />
                </Route>
                <Route path="templates" element={<Templates />} />
                <Route path="template-editor" element={<TemplateEditor />} />
                <Route path="template-editor/:templateId" element={<TemplateEditor />} />
                <Route path="edit-demo/:id" element={<EditDemoScenario />} />
                <Route path="datasets" element={<Datasets />} />
                <Route path="fields" element={<Fields />} />
                <Route path="fields/help-texts" element={<FieldHelpTexts />} />
                <Route path="fields/rules" element={<FieldRules />} />

                <Route
                  path={`${CLINICAL_CODE_RULES_ROUTES.clinicalCodeRules.slug}`}
                  element={<ClinicalCodeRulesPage />}
                />

                <Route path="demo-management" element={<DemoManagement />} />
                <Route path={`${CLINICAL_CODES_ROUTES.clinicalCoding.slug}`} element={<ClinicalCoding />} />
                <Route
                  path={`${CLINICAL_CODES_TRANSFORMATIONS_ROUTES.clinicalCodeTransformations.slug}`}
                  element={<ClinicalCodeTransformationsPage />}
                />
                <Route path="customer-management" element={<CustomerManagementPage />} />
                <Route path="encounter-mrn-lookup" element={<EncounterMrnLookup />} />
              </Route>

              {/* Test Data routes - accessible by admin and clinical_coder_admin */}
              <Route path={TEST_DATA_ROUTES.testData.slug} element={<TestDataGuard />}>
                <Route path="" element={<TestData />} />
                <Route
                  path={`${TEST_DATA_ROUTES.patientEncounterDetails.slug}/:encounter-id`}
                  element={<PatientEncounterDetails />}
                />
                <Route path="runs" element={<TestRuns />} />
                <Route path="runs/:runId" element={<RunDetails />} />
              </Route>

              <Route path="clinical-coding" element={<ClinicalCoderGuard />}>
                <Route
                  path={`${AI_CODING_OUTPUTS_ROUTES.aiCodingOutputs.slug}`}
                  element={<HumanReviewWorklistNext />}
                />
                <Route path="" element={<ClinicalCoderAdminGuard />}>
                  <Route path={`${ADMIN_CODING_ROUTES.codingDashboard.slug}`} element={<CodingDashboard />} />
                  <Route path={`${ADMIN_CODING_ROUTES.gapAnalysis.slug}`} element={<GapAnalysisSection />} />
                  <Route
                    path={`${AI_CODING_OUTPUTS_ROUTES.unwrittenEncounterReview.slug}`}
                    element={<UnwrittenEncounterReview />}
                  />
                  <Route
                    path={`${AI_CODING_OUTPUTS_ROUTES.unwrittenEncounterReview.slug}/:encounterId`}
                    element={<UnwrittenEncounterReview />}
                  />
                  <Route
                    path={WRITE_BACK_MANAGEMENT_ROUTES.writeBackManagement.slug}
                    element={<WriteBackManagementPage />}
                  />
                  <Route path={`${RAW_FILES_VIEWER_ROUTES.rawFilesViewer.slug}`} element={<RawFilesViewerPage />} />
                  <Route
                    path={`${RAW_FILES_VIEWER_ROUTES.rawFilesViewer.slug}/:encounterId`}
                    element={<RawFilesViewerEncounterPage />}
                  />
                  <Route
                    path={`${RELEVANCE_REVIEW_ROUTES.relevanceReview.slug}`}
                    element={<RelevanceReviewQueuePage />}
                  />
                  <Route
                    path={`${FORM_READ_CORRECTIONS_ROUTES.formReadCorrections.slug}`}
                    element={<FormReadCorrectionsPage />}
                  />
                  <Route
                    path={`${FORM_READ_CORRECTIONS_ROUTES.formReadCorrections.slug}/:encounterId`}
                    element={<FormReadCorrectionsEncounterPage />}
                  />
                </Route>
                <Route
                  path={`${AI_CODING_OUTPUTS_ROUTES.humanCodingReview.slug}/:id`}
                  element={<HumanCodingReview />}
                />
              </Route>
            </Route>

            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
